import { Component, OnInit, HostListener, inject } from '@angular/core';
import { Event as NavigationEvent, NavigationEnd, Router } from '@angular/router';
import { AccessType } from '@app/@shared/enums/access-types.enum';
import { SubdomainTypes } from '@app/@shared/enums/subdomain-types.enum';
import { ApplicationProfile } from '@app/@shared/models/application-profile.model';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { PortalThemeService } from '@app/@shared/services/portal-theme.service';
import { SubdomainService } from '@app/@shared/services/subdomain.service';
import { CredentialsService } from '@app/auth';
import { FONT_AWESOME_ICONS, isNotNullOrUndefined } from '@ep/shared';
import { faUsersCog } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, debounceTime, filter, Subscription, tap } from 'rxjs';
import { BreadcrumbService, SignalRService, WindowService } from '@app/resources/services';
import { Store } from '@ngrx/store';
import { ApplicationProfileActions, CredentialsActions } from '@app/resources/ngrx/actions';
import { SignalrTargetsEnum } from '@shared/enums';
import { RepresentativesApiService } from '@app/areas/admin/representatives/services/representatives.api.service';

@UntilDestroy()
@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements OnInit {
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly store = inject(Store);
  private readonly windowService = inject(WindowService);
  private readonly signalRService = inject(SignalRService);
  private readonly representativesApiService = inject(RepresentativesApiService);
  private readonly portalThemeService = inject(PortalThemeService);

  // Icons
  icons: { [index: string]: IconDefinition } = { ...FONT_AWESOME_ICONS, faUsersCog };

  // Component State.
  showMobileDropdown: boolean = false;
  showAdminSubmenu: boolean = false;

  // Application Data.
  AccessType = AccessType;
  applicationProfile!: ApplicationProfile;
  subdomainType!: SubdomainTypes | null;
  SubdomainTypes = SubdomainTypes;

  subs = new Subscription();

  constructor(
    private router: Router,
    private applicationProfileService: ApplicationProfileService,
    private credentialsService: CredentialsService,
    private subdomainService: SubdomainService
  ) {}

  private _resize$ = new BehaviorSubject<boolean>(false);
  readonly resize$ = this._resize$.asObservable().pipe(filter(isNotNullOrUndefined));

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this._resize$.next(true);
  }

  ngOnInit(): void {
    this.signalRService.hubConnection.on(SignalrTargetsEnum.onJwtChange, (credentials: any) => {
      this.credentialsService.setCredentials(credentials);
      this.store.dispatch(CredentialsActions.updateCredentials({ payload: { credentials } }));
    });

    this.signalRService.hubConnection.on(SignalrTargetsEnum.onRepresentativeChange, (data: any) => {
      this.representativesApiService.applyRepresentativeChange(data);
    });

    this.subs.add(
      this.resize$
        .pipe(
          debounceTime(300),
          tap(() => {
            this.windowService.checkScreenSize();

            document.documentElement.style.setProperty('--screenHeight', `${window.innerHeight}px`);
          })
        )
        .subscribe()
    );

    this.subdomainType = this.subdomainService.getDomain();
    this.windowService.checkScreenSize();
    this.subscribeToApplicationProfile();

    if (this.router.url === '/') {
      this.onNavigation('dashboard');
    }

    this.subs.add(
      this.router.events.pipe(filter((event: NavigationEvent) => event instanceof NavigationEnd)).subscribe(() => {
        this.breadcrumbService.updateBreadcrumbs(this.applicationProfile.Representative.CoalitionId);
      })
    );

    this.breadcrumbService.updateBreadcrumbs(this.applicationProfile.Representative.CoalitionId);
  }

  get breadcrumbs$() {
    return this.breadcrumbService.breadcrumbs$;
  }

  get home$() {
    return this.breadcrumbService.home$;
  }

  shouldShowBreadcrumbs$ = this.breadcrumbService.shouldShowBreadcrumbs$;

  get isMobileScreen() {
    return this.windowService.isMobileScreen;
  }

  get isSmallScreen() {
    return this.windowService.isSmallScreen;
  }

  get shouldShowFullWidth() {
    return this.windowService.shouldShowFullWidth;
  }

  set shouldShowFullWidth(showFullWidth: boolean) {
    this.windowService.setShowFullWidth(showFullWidth);
  }

  onCloseClicked(): void {
    this.shouldShowFullWidth = !this.shouldShowFullWidth;
  }

  onNavigation(resource: string) {
    this.router.navigateByUrl(`/${this.applicationProfile.Representative.CoalitionId}/${resource}`);
  }

  onNavigationTheme() {
    this.router.navigateByUrl(`/${this.applicationProfile.Representative.CoalitionId}/theme`);
  }

  onToggleMobileDropdown(): void {
    this.showMobileDropdown = !this.showMobileDropdown;
  }

  onToggleAdminSubmenu(): void {
    this.showAdminSubmenu = !this.showAdminSubmenu;
  }

  closeMobileDropdown(): void {
    this.showMobileDropdown = false;
  }

  shouldShowMenuItem(accessType: AccessType): boolean {
    return this.credentialsService.hasAccessAny([accessType]);
  }

  getImageSrc(): string {
    const isMinimized = (this.isSmallScreen && !this.shouldShowFullWidth) || !this.shouldShowFullWidth;
    return isMinimized ? this.getSecondaryLogoSrc() : this.getPrimaryLogoSrc();
  }

  getPrimaryLogoSrc(): string {
    const themeLogo = this.applicationProfile?.Representative?.PortalTheme?.PrimaryLogo;
    if (themeLogo) {
      return themeLogo;
    }
    const suffix = this.subdomainType === SubdomainTypes.EagleProcessing ? 'eagle-logo.png' : 'enroll-and-pay-logo.png';
    return `../../assets/${suffix}`;
  }

  getSecondaryLogoSrc(): string {
    const themeLogo = this.applicationProfile?.Representative?.PortalTheme?.SecondaryLogo;
    if (themeLogo) {
      return themeLogo;
    }
    const suffix = this.subdomainType === SubdomainTypes.EagleProcessing ? 'eagle-logo.png' : 'enroll-and-pay-logo.png';
    return `../../assets/small-${suffix}`;
  }

  private subscribeToApplicationProfile(): void {
    this.applicationProfileService.applicationProfile$
      .pipe(untilDestroyed(this))
      .subscribe((applicationProfile: ApplicationProfile) => {
        this.applicationProfile = applicationProfile;
        this.applyPortalTheme(applicationProfile);
        this.store.dispatch(
          ApplicationProfileActions.loadApplicationProfilesSuccess({ payload: { profile: applicationProfile } })
        );
      });
  }

  private applyPortalTheme(applicationProfile: ApplicationProfile): void {
    const portalTheme = applicationProfile?.Representative?.PortalTheme;
    if (portalTheme?.Theme) {
      this.portalThemeService.applyTheme(this.portalThemeService.mergeWithDefaultTheme(portalTheme.Theme));
    }
    this.applyThemeFavicon(portalTheme?.FavIcon);
  }

  private applyThemeFavicon(favIcon?: string): void {
    if (!favIcon) {
      return;
    }
    const link = document.querySelector<HTMLLinkElement>('#favicon');
    if (link) {
      link.href = favIcon;
    }
  }
}
